import { Fragment, type ReactNode } from 'react';

/**
 * The small subset of Markdown the agent actually writes: paragraphs, bold, italic, the two
 * together, inline code and the two list forms. Nothing else is recognised — a heading, a link,
 * an image or a run of HTML stays the literal text the model produced.
 *
 * It is hand-rolled and it renders **React elements**, never markup. That is the whole security
 * argument: a model's reply is untrusted text that a user may have pasted into it, so there is no
 * HTML string anywhere for it to escape out of, and `<script>` in a reply is a text node that
 * reads as `<script>`. A library would have to be trusted not to have an `html: true` path; an
 * element tree has no such path to leave switched off.
 */

/**
 * Code first, so backticks win over emphasis and nothing inside them is re-read as syntax. Then
 * the emphasis runs in order of delimiter length: `**` reaching a `***` run first would take two
 * of its asterisks and leave the third as literal text beside the emphasised word.
 *
 * The italic form additionally requires each delimiter to sit against a non-space, which is the
 * one CommonMark rule worth having here — without it a multiplication sign or a footnote marker
 * in a reply (`3 * 4 * 5`) is read as emphasis, and an unpaired `**` opens a run that swallows
 * the prose up to the next asterisk.
 */
const INLINE = /`([^`]+)`|\*\*\*([\s\S]+?)\*\*\*|\*\*([\s\S]+?)\*\*|\*(?![\s*])([^*\n]+?)(?<!\s)\*/;

const BULLET = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*\d+\.\s+(.*)$/;

const renderInline = (text: string, key: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let rest = text;
  let index = 0;

  while (rest !== '') {
    const match = INLINE.exec(rest);

    if (match === null) {
      nodes.push(rest);
      break;
    }

    if (match.index > 0) {
      nodes.push(rest.slice(0, match.index));
    }

    const childKey = `${key}i${index}`;
    const [whole, code, both, bold, italic] = match;

    if (code !== undefined) {
      nodes.push(<code key={childKey}>{code}</code>);
    } else if (both !== undefined) {
      nodes.push(
        <strong key={childKey}>
          <em>{renderInline(both, childKey)}</em>
        </strong>,
      );
    } else if (bold !== undefined) {
      nodes.push(<strong key={childKey}>{renderInline(bold, childKey)}</strong>);
    } else if (italic !== undefined) {
      nodes.push(<em key={childKey}>{renderInline(italic, childKey)}</em>);
    }

    rest = rest.slice(match.index + whole.length);
    index += 1;
  }

  return nodes;
};

/**
 * Blocks are line-based. A blank line ends whatever is open, a bullet or a numbered line opens or
 * continues a list, and everything else accretes into a paragraph whose own line breaks survive
 * because the paragraph is rendered `pre-wrap`.
 */
export const renderMarkdown = (text: string): ReactNode[] => {
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];
  let ordered = false;

  const flushParagraph = () => {
    if (paragraph.length === 0) {
      return;
    }

    const key = `p${blocks.length}`;
    blocks.push(<p key={key}>{renderInline(paragraph.join('\n'), key)}</p>);
    paragraph = [];
  };

  const flushList = () => {
    if (items.length === 0) {
      return;
    }

    const key = `l${blocks.length}`;
    // A position is the only identity a list item has here: the whole block is rebuilt from the
    // text on every delta, and no item holds state a reused key could carry into the wrong row.
    const rendered = items.map((item, index) => (
      // oxlint-disable-next-line react/no-array-index-key
      <li key={`${key}-${index}`}>{renderInline(item, `${key}-${index}`)}</li>
    ));

    blocks.push(ordered ? <ol key={key}>{rendered}</ol> : <ul key={key}>{rendered}</ul>);
    items = [];
  };

  for (const line of text.split('\n')) {
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    const numbered = ORDERED.exec(line);
    const item = numbered ?? BULLET.exec(line);

    if (item !== null) {
      flushParagraph();
      const nowOrdered = numbered !== null;

      // A list that changes form is a second list, not a continuation of the first.
      if (items.length > 0 && nowOrdered !== ordered) {
        flushList();
      }

      ordered = nowOrdered;
      items.push(item[1] ?? '');
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();

  return blocks;
};

/** The rendered subset, as one node a caller can drop into a message body. */
export const Markdown = ({ text }: { text: string }) => <Fragment>{renderMarkdown(text)}</Fragment>;
